import type { UniqueIdentifier } from "@dnd-kit/core"
import { useDroppable } from "@dnd-kit/core"
import { SortableContext } from "@dnd-kit/sortable"
import { FlexBox, SPACING } from "@joonapp/web-shared"
import type { ReactNode } from "react"
import React from "react"

import { SortableItem } from "./SortableItem"

interface BaseItem {
  id: UniqueIdentifier
}

interface Props<T extends BaseItem> {
  title: string
  items: T[]
  renderItem(item: T): ReactNode
}

export function SortableList<T extends BaseItem>({
  title,
  items,
  renderItem,
}: Props<T>) {
  const emptyDropZoneId = `${title}-empty-drop-zone`
  const { setNodeRef, isOver } = useDroppable({
    id: emptyDropZoneId,
    data: {
      type: "empty-section",
      section: title,
    },
  })

  return (
    <SortableContext id={title} items={items}>
      <FlexBox direction="column" gap={SPACING.space2} role="application">
        {items.map((item) => (
          <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
        ))}
        {items.length === 0 && (
          <div
            ref={setNodeRef}
            className={`w-full min-h-16 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors ${
              isOver
                ? "border-blue-400 bg-blue-50"
                : "border-gray-300 bg-gray-50"
            }`}
          >
            <span className="text-gray-400 text-sm">Empty</span>
          </div>
        )}
      </FlexBox>
    </SortableContext>
  )
}

SortableList.Item = SortableItem
